import { Helmet } from 'react-helmet-async';

interface StructuredDataProps {
  type?: 'homepage' | 'page' | 'article';
  title?: string;
  description?: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
}

export const StructuredData: React.FC<StructuredDataProps> = ({
  type = 'homepage',
  title,
  description,
  image,
  datePublished,
  dateModified,
  author,
}) => {
  const siteUrl = 'https://www.telegram-stars.io';

  // Organization Schema (always include)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Telegram Stars Payment Platform',
    alternateName: ['Telegram Stars', 'Stars Payment', 'TelegramStars', 'StarsPayment'],
    url: siteUrl,
    logo: `${siteUrl}/images/logo.png`,
    description:
      'Telegram Stars payment platform: pay with Telegram Stars or get paid with Stars payment links, ledger and TON withdrawals.',
    knowsAbout: [
      'Telegram Stars payment',
      'pay with Telegram Stars',
      'Stars payment',
      'Telegram Stars payment links',
      'TON withdrawals',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: 'support@telegram-stars.io',
    },
  };

  // SoftwareApplication Schema (for homepage)
  const softwareApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Telegram Stars Payment Platform',
    alternateName: ['Stars Payment', 'TelegramStars'],
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Telegram',
    description:
      'Create Telegram Stars payment links, accept Stars payments, track a ledger and withdraw to TON. Flat 3% fee.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '3',
        priceCurrency: 'USD',
        unitText: 'percent',
      },
    },
  };

  // FAQPage Schema (for homepage) — questions match Search Console / AI queries
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is a Telegram Stars payment?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A Telegram Stars payment is a purchase paid in Telegram Stars (XTR) through Telegram’s native checkout. Buyers confirm in the app; sellers get paid in Stars and can withdraw to a TON wallet.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I pay with Telegram Stars?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Open the seller’s Telegram Stars payment link or invoice, review the Stars amount, and confirm. If your balance is low, Telegram lets you buy Stars on the spot, then completes the payment.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I accept Stars payments on Telegram?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Create a free Stars payment link on telegram-stars.io, share it with customers, and track each payment in the dashboard. Flat 3% fee, no monthly cost, withdrawals to TON from 100 Stars after 21 days.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the platform fee?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The platform fee is 3% per transaction. There are no setup fees or monthly costs.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I withdraw my Telegram Stars?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Request a withdrawal to your TON wallet from the dashboard. Minimum withdrawal is 100 Stars. Telegram’s 21-day hold applies before Stars become withdrawable.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is Telegram Stars payment secure?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "Yes. Payments are processed through Telegram’s payment system. The platform adds a ledger, redirects and withdrawal tooling on top.",
        },
      },
    ],
  };

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      ...(title
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: title,
              item: siteUrl,
            },
          ]
        : []),
    ],
  };

  // Article Schema (if type is article)
  const articleSchema =
    type === 'article' && title
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: title,
          author: author
            ? {
                '@type': 'Person',
                name: author,
              }
            : {
                '@type': 'Organization',
                name: 'Telegram Stars Payment Platform',
              },
          datePublished: datePublished || new Date().toISOString(),
          dateModified: dateModified || new Date().toISOString(),
          publisher: {
            '@type': 'Organization',
            name: 'Telegram Stars Payment Platform',
            logo: {
              '@type': 'ImageObject',
              url: `${siteUrl}/images/logo.png`,
            },
          },
          image: image || `${siteUrl}/images/og-image.png`,
          description: description,
        }
      : null;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      {type === 'homepage' && (
        <>
          <script type="application/ld+json">{JSON.stringify(softwareApplicationSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </>
      )}
      {title && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
      {articleSchema && <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>}
    </Helmet>
  );
};
